import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import GoldSeam from "@/components/GoldSeam";
import { LETTERS, getLetter } from "@/data/letters";

const fmt = (d) =>
  new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

export default function LetterPost() {
  const { slug } = useParams();
  const letter = getLetter(slug);

  if (!letter) {
    return (
      <main className="pt-20" data-testid="letter-not-found">
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <h1 className="font-display text-4xl text-ink">That letter isn't here.</h1>
          <Link
            to="/letters"
            className="gold-underline-link inline-block mt-8 text-[11px] uppercase tracking-[0.24em] text-gold"
            data-testid="letter-back-link"
          >
            Back to all letters
          </Link>
        </div>
      </main>
    );
  }

  const next = LETTERS[(LETTERS.findIndex((l) => l.slug === slug) + 1) % LETTERS.length];

  return (
    <main className="pt-20" data-testid="letter-post-page">
      <article>
        <section className="py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-6">
            <Reveal>
              <Link
                to="/letters"
                className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-inkdim hover:text-gold transition-colors duration-300"
                data-testid="letter-back-link"
              >
                <ArrowLeft size={12} strokeWidth={1.6} /> Kintsugi Letters
              </Link>
              <p className="mt-9 text-[10px] uppercase tracking-[0.24em] text-gold">
                {fmt(letter.date)} · {letter.read} read
              </p>
              <h1
                className="mt-5 font-display text-ink font-medium leading-[1.08]"
                style={{ fontSize: "clamp(2.2rem, 4.8vw, 3.6rem)" }}
                data-testid="letter-title"
              >
                {letter.title}
              </h1>
              <p className="mt-6 font-display italic text-goldink text-xl md:text-2xl leading-snug">
                {letter.dek}
              </p>
              {letter.author && (
                <div className="mt-9 flex items-center gap-4" data-testid="letter-byline">
                  <img
                    src="/images/rajdeep-avatar.jpg"
                    alt={letter.author}
                    loading="lazy"
                    className="w-12 h-12 rounded-full object-cover border border-[rgba(169,129,47,0.4)]"
                  />
                  <span>
                    <span className="block text-sm text-ink">{letter.author}</span>
                    <span className="block text-[10px] uppercase tracking-[0.24em] text-inkdim mt-1">
                      Founder, T.A.L.K.
                    </span>
                  </span>
                </div>
              )}
            </Reveal>
          </div>
        </section>

        <Reveal>
          <div className="max-w-5xl mx-auto px-6">
            <div className="relative border border-[rgba(169,129,47,0.26)] p-3">
              <span className="absolute -top-px -left-px w-5 h-5 border-t border-l border-gold" aria-hidden="true" />
              <img
                src={letter.image}
                alt={letter.title}
                className="w-full h-[280px] md:h-[440px] object-cover"
                data-testid="letter-image"
              />
            </div>
          </div>
        </Reveal>

        <section className="py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-6">
            {letter.body.map((p, i) => (
              <Reveal key={i} delay={0.02}>
                <p
                  className={`text-inkdim leading-[1.85] text-[16px] md:text-[17px] mb-8 whitespace-pre-line ${
                    i === 0 && !p.includes("\n")
                      ? "first-letter:font-display first-letter:text-6xl first-letter:text-gold first-letter:float-left first-letter:mr-3 first-letter:leading-[0.85]"
                      : ""
                  }`}
                  data-testid={`letter-paragraph-${i}`}
                >
                  {p}
                </p>
              </Reveal>
            ))}

            {letter.closing && (
              <Reveal>
                <blockquote
                  className="my-12 border-l-2 border-gold pl-7 font-display italic text-goldink text-2xl md:text-3xl leading-snug whitespace-pre-line"
                  data-testid="letter-pull-quote"
                >
                  "{letter.closing}"
                </blockquote>
              </Reveal>
            )}

            {letter.coda && (
              <Reveal>
                <p
                  className="text-ink leading-[1.85] text-[16px] md:text-[17px] whitespace-pre-line"
                  data-testid="letter-coda"
                >
                  {letter.coda}
                </p>
              </Reveal>
            )}

            {letter.author && (
              <Reveal>
                <p className="mt-10 text-[11px] uppercase tracking-[0.28em] text-gold" data-testid="letter-signoff">
                  — {letter.author} · T.A.L.K. The Analog Life Kintsugi
                </p>
              </Reveal>
            )}

            <GoldSeam variant="horizontal" className="mt-12 w-48 h-10 opacity-70" delay={0.2} />

            <Reveal>
              <div className="mt-12 border border-[rgba(169,129,47,0.26)] bg-paper p-8" data-testid="letter-cta">
                <p className="eyebrow mb-4">If this landed</p>
                <p className="font-display text-ink text-2xl md:text-3xl leading-snug">
                  The pause is easier to practise out loud, with someone listening for the pattern.
                </p>
                <Link
                  to="/booking"
                  data-testid="letter-book-session-link"
                  className="inline-block mt-7 bg-deep text-canvas text-xs uppercase tracking-[0.24em] font-semibold px-8 py-4 hover:bg-gold transition-colors duration-300"
                >
                  Book a Session
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-paper border-t border-[rgba(169,129,47,0.26)]">
          <div className="max-w-3xl mx-auto px-6">
            <p className="eyebrow mb-6">Next letter</p>
            <Link to={`/letters/${next.slug}`} data-testid="letter-next-link" className="group block">
              <h2 className="font-display text-ink font-medium leading-[1.12] text-3xl md:text-4xl group-hover:text-gold transition-colors duration-500">
                {next.title}
              </h2>
              <span className="inline-flex items-center gap-2 mt-6 text-[10px] uppercase tracking-[0.24em] text-gold group-hover:gap-3 transition-all duration-500">
                Read <ArrowRight size={12} strokeWidth={1.5} />
              </span>
            </Link>
          </div>
        </section>
      </article>
    </main>
  );
}
